/**
 * Markdown directive plugin for mentions.
 *
 * This module provides remark-directive plugins for parsing and rendering
 * mentions in markdown content, enabling the :mention[name]{sId=xxx} syntax.
 */

import { useConversationSidePanelContext } from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { MentionDisplay } from "@app/components/mentions/MentionDisplay";
import { AGENT_SIDE_PANEL_TYPE } from "@app/types/conversation_side_panel";
import type { WorkspaceType } from "@app/types/user";
import { visit } from "unist-util-visit";

/*
 * Remark directive plugin for parsing mention directives.
 *
 * Transforms `:mention[name]{sId=xxx}` into a custom HTML element
 * that can be rendered by the mention component.
 *
 * Note: We cannot easily rename "mention" to "agent_mention" because
 * the messages stored in the database use this name.
 */
export function agentMentionDirective() {
  return (tree: any) => {
    visit(tree, ["textDirective"], (node) => {
      if (node.name === "mention" && node.children[0]) {
        const data = node.data || (node.data = {});
        data.hName = "mention";
        data.hProperties = {
          agentId: node.attributes.sId,
          agentName: node.children[0].value,
        };
      }
    });
  };
}

/**
 * Creates a React component plugin for rendering mentions in markdown.
 *
 * This function returns a component that can be used as a custom component
 * in ReactMarkdown to render the mention HTML elements.
 *
 * @param owner - The workspace context for mention interactions
 * @returns A React component for rendering mentions
 */
export function getAgentMentionPlugin(owner: WorkspaceType) {
  const AgentMentionPlugin = ({
    agentName,
    agentId,
  }: {
    agentName: string;
    agentId: string;
  }) => {
    const { openPanel } = useConversationSidePanelContext();

    return (
      <MentionDisplay
        mention={{
          id: agentId,
          label: agentName,
          type: "agent",
          pictureUrl: "",
          description: "",
        }}
        interactive
        owner={owner}
        showTooltip={false}
        onSeeAgentDetails={() =>
          openPanel({ type: AGENT_SIDE_PANEL_TYPE, agentId })
        }
      />
    );
  };

  return AgentMentionPlugin;
}

/*
 * Remark directive plugin for parsing user mention directives.
 *
 * Transforms `:mention_user[name]{sId=xxx}` into a custom HTML element
 * that can be rendered by the mention component.
 *
 */
export function userMentionDirective() {
  return (tree: any) => {
    visit(tree, ["textDirective"], (node) => {
      if (node.name === "mention_user" && node.children[0]) {
        const data = node.data || (node.data = {});
        data.hName = "mention_user";
        data.hProperties = {
          userId: node.attributes.sId,
          userName: node.children[0].value,
        };
      }
    });
  };
}

/**
 * Creates a React component plugin for rendering user mentions in markdown.
 *
 * This function returns a component that can be used as a custom component
 * in ReactMarkdown to render the mention HTML elements.
 *
 * @param owner - The workspace context for mention interactions
 * @returns A React component for rendering mentions
 */
export function getUserMentionPlugin(owner: WorkspaceType) {
  const UserMentionPlugin = ({
    userName,
    userId,
  }: {
    userName: string;
    userId: string;
  }) => {
    return (
      <MentionDisplay
        mention={{
          id: userId,
          label: userName,
          type: "user",
          pictureUrl: "",
          description: "",
        }}
        interactive
        owner={owner}
        showTooltip={false}
      />
    );
  };

  return UserMentionPlugin;
}
